import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { BrtNode } from 'react-bunja-devtools/brt';

import { css } from '@flipster/styled-system/css';
import { flex, hstack, stack } from '@flipster/styled-system/patterns';

import { BrtNodeNoContext, brtBunja, brtNodeBunja } from '../state';

export const TreeView = () => {
  const { reloadCountAtom, brtAtom } = useBunja(brtBunja);
  const reloadCount = useAtomValue(reloadCountAtom);
  const brt = useAtomValue(brtAtom);
  return (
    <div className={css({ flex: 1, overflow: 'scroll', minH: 0 })}>
      <div
        className={flex({
          gap: 12,
          px: 16,
          my: 8,
          pb: '50vh',
          textStyle: 'Text/M/Monospace/Regular',
          lineHeight: '16px',
          background: `repeating-linear-gradient(
              to bottom,
              {colors.background.default.level-0} 0 16px,
              color-mix(in srgb, {colors.background.default.level-4} 2%, transparent) 16px 32px
            )`,
        })}
      >
        <div
          className={css({
            textAlign: 'right',
            userSelect: 'none',
            textStyle: 'Text/S/Monospace/Regular',
            color: 'content.default.level-1',
          })}
        >
          {brt.map((node) => (
            <BrtNodeNoContext value={node.no} key={node.no}>
              <NodeNumber node={node} />
            </BrtNodeNoContext>
          ))}
        </div>
        <div key={reloadCount}>
          {brt.map((node) => (
            <BrtNodeNoContext value={node.no} key={node.no}>
              <Node node={node} />
            </BrtNodeNoContext>
          ))}
        </div>
      </div>
    </div>
  );
};

const ContextNode = ({ node }: NodeProps) => {
  const { isSelectedAtom, select } = useBunja(brtNodeBunja);
  const isSelected = useAtomValue(isSelectedAtom);
  if (node.type !== 'context') return null;
  const contextCount = node.contexts.length;
  return (
    <div className={hstack({ textStyle: 'Text/XS/Monospace/Regular' })}>
      <button
        onClick={select}
        className={css({
          cursor: 'pointer',
          border: 'none',
          bg: isSelected ? 'background.accent.info' : 'transparent',
          color: isSelected ? 'content.accent.info' : 'content.default.level-3',
          px: 4,
          py: 1,
          rounded: 3,
          transition: 'all 0.15s ease',
          textStyle: 'Text/XS/Monospace/Regular',
          _hover: {
            bg: isSelected ? 'background.accent.info' : 'background.default.level-2',
          },
        })}
      >
        &lt;Context × {contextCount}&gt;
      </button>
    </div>
  );
};

const ComponentNode = ({ node }: NodeProps) => {
  const { isSelectedAtom, select } = useBunja(brtNodeBunja);
  const isSelected = useAtomValue(isSelectedAtom);
  if (node.type !== 'component') return null;
  const useBunjaCount = node.bunjaHooks.length;
  return (
    <div className={hstack({ color: 'content.default.level-5' })}>
      <button
        onClick={select}
        className={css({
          cursor: 'pointer',
          border: 'none',
          bg: isSelected ? 'background.accent.info' : 'transparent',
          color: isSelected ? 'content.accent.info' : 'content.default.level-5',
          px: 4,
          py: 1,
          rounded: 3,
          transition: 'all 0.1s ease',
          _hover: {
            bg: isSelected ? 'background.accent.info' : 'background.default.level-2',
          },
        })}
      >
        <span>&lt;{node.name || '[Anonymous]'}&gt;</span>
      </button>
      <span
        className={css({
          ml: 6,
          textStyle: 'Text/XS/Monospace/Regular',
          color: 'content.default.level-1',
        })}
      >
        useBunja × {useBunjaCount}
      </span>
    </div>
  );
};

interface NodeProps {
  node: BrtNode;
}
const Node = ({ node }: NodeProps) => {
  const { isCollapsedAtom, toggleIsCollapsed } = useBunja(brtNodeBunja);
  const isCollapsed = useAtomValue(isCollapsedAtom);
  const hasChildren = node.children.length > 0;
  return (
    <div className={stack()}>
      <div className={hstack({ gap: 4, alignItems: 'center', h: 16 })}>
        <button
          onClick={toggleIsCollapsed}
          disabled={!hasChildren}
          className={css({
            ml: '-5px',
            cursor: 'pointer',
            border: 'none',
            bg: 'transparent',
            padding: 0,
            color: 'content.default.level-2',
            fontSize: '8px',
            minW: '12px',
            textAlign: 'center',
            transition: 'transform 0.2s ease',
            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
            _hover: { color: 'content.default.level-4' },
            _disabled: { opacity: 0.5, cursor: 'not-allowed' },
          })}
        >
          ▶
        </button>
        {node.type === 'component' ? <ComponentNode node={node} /> : <ContextNode node={node} />}
      </div>
      {hasChildren && !isCollapsed && (
        <div className={css({ borderLeft: '1px solid', borderLeftColor: 'border.accent.info', pl: '1em' })}>
          {node.children.map((child) => (
            <BrtNodeNoContext value={child.no} key={child.no}>
              <Node key={child.no} node={child} />
            </BrtNodeNoContext>
          ))}
        </div>
      )}
    </div>
  );
};

const NodeNumber = ({ node }: NodeProps) => {
  const { isCollapsedAtom } = useBunja(brtNodeBunja);
  const isCollapsed = useAtomValue(isCollapsedAtom);
  return (
    <div className={stack()}>
      <span className={css({ h: 16 })}>{node.no}</span>
      {!isCollapsed &&
        node.children.map((child) => (
          <BrtNodeNoContext value={child.no} key={child.no}>
            <NodeNumber key={child.no} node={child} />
          </BrtNodeNoContext>
        ))}
    </div>
  );
};
